// Domain 02 IOS features: VLAN lists, 802.1Q trunk options, EtherChannel,
// spanning tree, SVIs / multilayer switching, router-on-a-stick subinterfaces.
// Command specs here are merged into the per-mode tries in cli.js.

import { dev, ifStatus, linkOf, linkState } from '../net/sim.js';
import {
  stpVlan, channelMembers, memberFlag, logicalPorts, isPo, isSvi, carries, protocolOf, physFor, bridgePriority,
} from '../net/l2.js';
import { macCisco } from '../net/addr.js';
import { shortIf, ifSort } from './ifnames.js';

const pad = (s, n) => String(s).padEnd(n);
const K = (kinds, specs) => specs.map((x) => ({ ...x, kinds }));

// ------------------------------------------------------------ VLAN lists
export function parseVlanList(str) {
  const out = new Set();
  for (const part of String(str).split(',')) {
    const m = part.match(/^(\d+)(?:-(\d+))?$/);
    if (!m) return null;
    const a = +m[1], b = m[2] ? +m[2] : a;
    if (a < 1 || b > 4094 || b < a) return null;
    for (let v = a; v <= b; v++) out.add(v);
  }
  return [...out].sort((x, y) => x - y);
}
export function fmtVlanList(list) {
  if (!list) return '1-4094';
  if (!list.length) return 'none';
  const out = [];
  let s = list[0], p = list[0];
  for (const v of [...list.slice(1), null]) {
    if (v === p + 1) { p = v; continue; }
    out.push(s === p ? `${s}` : `${s}-${p}`);
    s = p = v;
  }
  return out.join(',');
}
const ALL = () => Array.from({ length: 4094 }, (_, i) => i + 1);

// ------------------------------------------------------------ interface factories
const counters = () => ({ inPkts: 0, outPkts: 0, crc: 0, inErrors: 0 });
export function createInterface(d, name) {
  if (d.interfaces[name]) return d.interfaces[name];
  if (d.kind === 'switch' && isSvi(name)) {
    d.interfaces[name] = { name, type: 'svi', shutdown: name !== 'Vlan1', description: '', ip: null, mac: d.baseMac.slice(0, 8) + (0x40 + (+name.slice(4) % 190)).toString(16).padStart(4, '0'), counters: counters(), ipv6: [], nat: null };
  } else if (d.kind === 'switch' && isPo(name)) {
    d.interfaces[name] = { name, type: 'po', shutdown: false, description: '', mode: 'access', accessVlan: 1, mac: d.baseMac.slice(0, 8) + '0' + (0xc0 + +name.replace('Port-channel', '')).toString(16).padStart(3, '0'), counters: counters(), speed: 'auto', duplex: 'auto', ip: null, ipv6: [] };
  } else if (d.kind === 'router' && /^Loopback\d+$/.test(name)) {
    d.interfaces[name] = { name, type: 'loopback', shutdown: false, description: '', ip: null, mac: '', counters: counters(), nat: null, ipv6: [], mode: 'routed', speed: 'auto', duplex: 'auto' };
  } else if (d.kind === 'router' && /^Tunnel\d+$/.test(name)) {
    // IOS creates tunnel interfaces "up" (no shutdown) with GRE as the default mode.
    d.interfaces[name] = { name, type: 'tunnel', shutdown: false, description: '', ip: null, mac: '', counters: counters(), nat: null, ipv6: [], mode: 'routed', speed: 'auto', duplex: 'auto', tunnel: { source: null, destination: null, mode: 'gre', protection: null } };
  } else if (d.kind === 'router') {
    const parent = d.interfaces[name.replace(/\.\d+$/, '')];
    d.interfaces[name] = { name, type: 'subif', shutdown: false, description: '', ip: null, encap: null, mac: parent.mac, counters: counters(), nat: null, ipv6: [], mode: 'routed', speed: 'auto', duplex: 'auto' };
  }
  return d.interfaces[name];
}

// ------------------------------------------------------------ show renderers
export function trunkDetail(net, id) {
  const d = dev(net, id);
  const trunks = logicalPorts(net, id).filter((lp) => d.interfaces[lp].mode === 'trunk').sort(ifSort);
  // Down trunks are still listed by IOS only when they are trunking; keep it to operational ones.
  const rows = trunks.filter((lp) => ifStatus(net, id, lp).connected || isPo(lp));
  if (!rows.length) return [];
  const stp = {};
  const line = (lp, txt) => `${pad(shortIf(lp), 12)}${txt}`;
  const out = ['', 'Port        Mode             Encapsulation  Status        Native vlan'];
  for (const lp of rows) {
    const i = d.interfaces[lp];
    out.push(line(lp, `${pad('on', 17)}${pad('802.1q', 15)}${pad(ifStatus(net, id, lp).connected ? 'trunking' : 'not-trunking', 14)}${i.nativeVlan || 1}`));
  }
  out.push('', 'Port        Vlans allowed on trunk');
  for (const lp of rows) out.push(line(lp, fmtVlanList(d.interfaces[lp].allowed || null)));
  const active = (lp) => (d.interfaces[lp].allowed || Object.keys(d.vlans).map(Number)).filter((v) => d.vlans[v] && v < 1002).sort((a, b) => a - b);
  out.push('', 'Port        Vlans allowed and active in management domain');
  for (const lp of rows) out.push(line(lp, fmtVlanList(active(lp)) === 'none' ? 'none' : fmtVlanList(active(lp))));
  out.push('', 'Port        Vlans in spanning tree forwarding state and not pruned');
  for (const lp of rows) {
    const f = active(lp).filter((v) => { const t = (stp[v] = stp[v] || stpVlan(net, v)); const p = t.ports[`${id}|${lp}`]; return p && p.state === 'FWD'; });
    out.push(line(lp, f.length ? fmtVlanList(f) : 'none'));
  }
  return out;
}

export function switchportDetail(net, id, n) {
  const d = dev(net, id);
  const i = d.interfaces[n];
  const vname = (v) => `${v} (${d.vlans[v]?.name || 'Inactive'})`;
  const trunk = i.mode === 'trunk';
  const cg = i.channelGroup;
  return [
    `Name: ${shortIf(n)}`,
    'Switchport: Enabled',
    `Administrative Mode: ${trunk ? 'trunk' : 'static access'}`,
    `Operational Mode: ${trunk ? 'trunk' : 'static access'}${cg && memberFlag(net, id, n) === 'P' ? ` (member of bundle Po${cg.id})` : ''}`,
    `Administrative Trunking Encapsulation: ${d.l3 ? (i.trunkEncap || 'negotiate') : 'dot1q'}`,
    `Operational Trunking Encapsulation: ${trunk ? 'dot1q' : 'native'}`,
    `Negotiation of Trunking: ${i.nonegotiate ? 'Off' : 'On'}`,
    `Access Mode VLAN: ${vname(i.accessVlan || 1)}`,
    `Trunking Native Mode VLAN: ${vname(i.nativeVlan || 1)}`,
    'Administrative Native VLAN tagging: enabled',
    'Voice VLAN: none',
    'Administrative private-vlan host-association: none',
    'Operational private-vlan: none',
    `Trunking VLANs Enabled: ${i.allowed ? fmtVlanList(i.allowed) : 'ALL'}`,
    'Pruning VLANs Enabled: 2-1001',
    'Capture Mode Disabled',
    'Protected: false',
    'Unknown unicast blocked: disabled',
    'Unknown multicast blocked: disabled',
  ];
}

export function etherchannelSummary(net, id) {
  const d = dev(net, id);
  const pos = Object.keys(d.interfaces).filter(isPo).sort(ifSort);
  const out = [
    'Flags:  D - down        P - bundled in port-channel',
    '        I - stand-alone s - suspended',
    '        H - Hot-standby (LACP only)',
    '        R - Layer3      S - Layer2',
    '        U - in use      f - failed to allocate aggregator',
    '',
    '        M - not in use, minimum links not met',
    '        u - unsuitable for bundling',
    '        w - waiting to be aggregated',
    '        d - default port',
    '',
    '',
    `Number of channel-groups in use: ${pos.length}`,
    `Number of aggregators:           ${pos.length}`,
    '',
    'Group  Port-channel  Protocol    Ports',
    '------+-------------+-----------+-----------------------------------------------',
  ];
  for (const po of pos) {
    const n = po.replace('Port-channel', '');
    const mem = channelMembers(net, id, po);
    const up = ifStatus(net, id, po).connected;
    const proto = mem.length ? protocolOf(mem[0].mode) : '-';
    out.push(`${pad(n, 7)}${pad(`Po${n}(S${up ? 'U' : 'D'})`, 14)}${pad(proto, 12)}${mem.map((m) => `${shortIf(m.ifc)}(${m.flag})`).join(' ')}`);
  }
  return out;
}

function stpBlock(net, id, v, t) {
  const d = dev(net, id);
  const B = t.bridges[id];
  if (!B) return [];
  const rapid = d.stp?.mode === 'rapid-pvst';
  const rp = B.rootBridge;
  const hello = '             Hello Time   2 sec  Max Age 20 sec  Forward Delay 15 sec';
  const out = [`VLAN${String(v).padStart(4, '0')}`, `  Spanning tree enabled protocol ${rapid ? 'rstp' : 'ieee'}`,
    `  Root ID    Priority    ${rp.num}`, `             Address     ${macCisco(rp.mac)}`];
  if (B.isRoot) out.push('             This bridge is the root');
  else out.push(`             Cost        ${B.rootCost}`, `             Port        ${t.ports[`${id}|${B.rootPort}`].num} (${B.rootPort})`);
  out.push(hello, '', `  Bridge ID  Priority    ${B.num}  (priority ${B.prio} sys-id-ext ${v})`, `             Address     ${macCisco(B.mac)}`, hello, '             Aging Time  300 sec', '',
    'Interface           Role Sts Cost      Prio.Nbr Type', '------------------- ---- --- --------- -------- --------------------------------');
  const ports = Object.values(t.ports).filter((p) => p.sw === id).sort((a, b) => ifSort(a.lp, b.lp));
  for (const p of ports) {
    const sts = p.state === 'BLK' && !rapid ? 'BLK' : p.state;
    out.push(`${pad(shortIf(p.lp), 20)}${pad(p.role, 5)}${pad(sts, 4)}${pad(p.cost, 10)}${pad(`${p.pri}.${p.num}`, 9)}${p.type}`);
  }
  out.push('');
  return out;
}
export function spanningTree(net, id, onlyV) {
  const d = dev(net, id);
  const vlans = Object.keys(d.vlans).map(Number).filter((v) => v < 1002 && (!onlyV || v === onlyV)).sort((a, b) => a - b);
  if (onlyV && !d.vlans[onlyV]) return [`Spanning tree instance(s) for vlan ${onlyV} does not exist.`];
  const out = [];
  for (const v of vlans) {
    const t = stpVlan(net, v);
    if (!Object.values(t.ports).some((p) => p.sw === id)) continue; // no active ports → instance not shown
    out.push(...stpBlock(net, id, v, t));
  }
  if (!out.length) return onlyV ? [`Spanning tree instance(s) for vlan ${onlyV} does not exist.`] : ['No spanning tree instance exists.'];
  return out;
}
export function spanningTreeRoot(net, id) {
  const d = dev(net, id);
  const out = ['', '                                        Root    Hello Max Fwd', 'Vlan                   Root ID          Cost    Time  Age Dly  Root Port', '---------------- -------------------- --------- ----- --- ---  ------------'];
  for (const v of Object.keys(d.vlans).map(Number).filter((x) => x < 1002).sort((a, b) => a - b)) {
    const t = stpVlan(net, v);
    const B = t.bridges[id];
    if (!B || !Object.values(t.ports).some((p) => p.sw === id)) continue;
    out.push(`${pad(`VLAN${String(v).padStart(4, '0')}`, 17)}${pad(`${B.rootBridge.num} ${macCisco(B.rootBridge.mac)}`, 21)}${pad(B.rootCost, 10)}${pad('2', 6)}${pad('20', 4)}${pad('15', 5)}${B.rootPort ? shortIf(B.rootPort) : ''}`);
  }
  return out;
}

// ------------------------------------------------------------ command handlers
function setAllowed(s, fn) {
  return s.eachIf((i) => {
    const cur = i.allowed ? [...i.allowed] : null;
    i.allowed = fn(cur);
    if (i.allowed && i.allowed.length === 4094) i.allowed = null;
  });
}
function stpPriority(s, list, prio) {
  const vl = parseVlanList(list);
  if (!vl) return s.invalidAt();
  if (prio % 4096) {
    return ['% Bridge Priority must be in increments of 4096.', '% Allowed values are:', '  0     4096  8192  12288 16384 20480 24576 28672', '  32768 36864 40960 45056 49152 53248 57344 61440'];
  }
  s.d.stp.priority = s.d.stp.priority || {};
  for (const v of vl) s.d.stp.priority[v] = prio;
  return [];
}
function stpRootMacro(s, list, secondary) {
  const vl = parseVlanList(list);
  if (!vl) return s.invalidAt();
  s.d.stp.priority = s.d.stp.priority || {};
  for (const v of vl) {
    if (secondary) { s.d.stp.priority[v] = 28672; continue; }
    // root primary: 24576, or 4096 below the current root if that is already lower.
    const t = stpVlan(s.net, v);
    const cur = t.bridges[s.id]?.rootBridge;
    const other = cur && t.bridges[s.id]?.root !== s.id ? bridgePriority(dev(s.net, t.bridges[s.id].root), v) : 32768;
    s.d.stp.priority[v] = other > 24576 ? 24576 : Math.max(0, other - 4096);
  }
  return [];
}
function channelGroup(s, id, mode) {
  const d = s.d;
  const poName = `Port-channel${id}`;
  const out = [];
  for (const n of s.state.ifs) {
    const i = d.interfaces[n];
    if (i.channelGroup && i.channelGroup.id !== id) return [`Command rejected (${poName}, ${shortIf(n)}): the interface is already part of channel-group ${i.channelGroup.id}`];
    const peersMode = Object.values(d.interfaces).find((x) => x.channelGroup?.id === id && x !== i)?.channelGroup.mode;
    if (peersMode && protocolOf(peersMode) !== protocolOf(mode)) return [`Command rejected (Channel protocol mismatch for interface ${shortIf(n)} in group ${id}): the interface can not be added to the channel group`];
    if (!d.interfaces[poName]) {
      const po = createInterface(d, poName);
      Object.assign(po, { mode: i.mode, accessVlan: i.accessVlan, nativeVlan: i.nativeVlan, allowed: i.allowed ? [...i.allowed] : undefined, explicitAccess: i.explicitAccess, trunkEncap: i.trunkEncap });
      out.push(`Creating a port-channel interface ${poName}`, '');
    }
    i.channelGroup = { id, mode };
  }
  return out;
}

export const SW_SHOW = K(['switch'], [
  { pattern: 'show spanning-tree', run: (s) => spanningTree(s.net, s.id) },
  { pattern: 'show spanning-tree vlan <1-4094>', run: (s, [v]) => spanningTree(s.net, s.id, +v) },
  { pattern: 'show spanning-tree root', run: (s) => spanningTreeRoot(s.net, s.id) },
  { pattern: 'show etherchannel summary', run: (s) => etherchannelSummary(s.net, s.id) },
  { pattern: 'show interfaces <IF> switchport', run: (s, [i]) => switchportDetail(s.net, s.id, s.ifn(i)) },
  { pattern: 'show interfaces trunk', run: (s) => trunkDetail(s.net, s.id) },
]);

export const SW_CONFIG = K(['switch'], [
  { pattern: 'spanning-tree mode pvst', run: (s) => { s.d.stp.mode = 'pvst'; return []; } },
  { pattern: 'spanning-tree mode rapid-pvst', run: (s) => { s.d.stp.mode = 'rapid-pvst'; return []; } },
  { pattern: 'spanning-tree vlan <VLANLIST> priority <0-61440>', run: (s, [l, p]) => stpPriority(s, l, +p) },
  { pattern: 'spanning-tree vlan <VLANLIST> root primary', run: (s, [l]) => stpRootMacro(s, l, false) },
  { pattern: 'spanning-tree vlan <VLANLIST> root secondary', run: (s, [l]) => stpRootMacro(s, l, true) },
  { pattern: 'no spanning-tree vlan <VLANLIST> priority', run: (s, [l]) => { for (const v of parseVlanList(l) || []) delete s.d.stp.priority[v]; return []; } },
  { pattern: 'no spanning-tree vlan <VLANLIST> root', run: (s, [l]) => { for (const v of parseVlanList(l) || []) delete s.d.stp.priority[v]; return []; } },
  { pattern: 'ip default-gateway <A.B.C.D>', run: (s, [a]) => { s.d.defaultGateway = a; return []; } },
  { pattern: 'no ip default-gateway', run: (s) => { s.d.defaultGateway = null; return []; } },
  { pattern: 'ip routing', run: (s) => (s.d.l3 ? ((s.d.ipRouting = true), []) : s.caret(3)) },
  { pattern: 'no ip routing', run: (s) => (s.d.l3 ? ((s.d.ipRouting = false), []) : s.caret(6)) },
  { pattern: 'ip route <A.B.C.D> <A.B.C.D> <A.B.C.D>', run: (s, [n, m, h]) => (s.d.l3 ? s.addRoute(n, m, { nh: h }) : s.caret(3)) },
  { pattern: 'no ip route <A.B.C.D> <A.B.C.D> <A.B.C.D>', run: (s, [n, m, h]) => s.delRoute(n, m, { nh: h }) },
]);

const needSvi = (s, fn) => (isSvi(s.state.ifs[0]) ? fn() : ["% Invalid input detected at '^' marker."]);
export const SW_IF = K(['switch'], [
  { pattern: 'switchport trunk allowed vlan <VLANLIST>', run: (s, [l]) => { const v = parseVlanList(l); return v ? setAllowed(s, () => v) : s.invalidAt(); } },
  { pattern: 'switchport trunk allowed vlan add <VLANLIST>', run: (s, [l]) => { const v = parseVlanList(l); return v ? setAllowed(s, (cur) => (cur ? [...new Set([...cur, ...v])].sort((a, b) => a - b) : null)) : s.invalidAt(); } },
  { pattern: 'switchport trunk allowed vlan remove <VLANLIST>', run: (s, [l]) => { const v = parseVlanList(l); return v ? setAllowed(s, (cur) => (cur || ALL()).filter((x) => !v.includes(x))) : s.invalidAt(); } },
  { pattern: 'switchport trunk allowed vlan all', run: (s) => setAllowed(s, () => null) },
  { pattern: 'switchport trunk allowed vlan none', run: (s) => setAllowed(s, () => []) },
  { pattern: 'no switchport trunk allowed vlan', run: (s) => setAllowed(s, () => null) },
  { pattern: 'no switchport trunk native vlan', run: (s) => s.eachIf((i) => { delete i.nativeVlan; }) },
  { pattern: 'switchport trunk encapsulation dot1q', run: (s) => (s.d.l3 ? s.eachIf((i) => { i.trunkEncap = 'dot1q'; }) : s.caret(21)) },
  { pattern: 'switchport nonegotiate', run: (s) => s.eachIf((i) => { i.nonegotiate = true; }) },
  { pattern: 'no switchport nonegotiate', run: (s) => s.eachIf((i) => { i.nonegotiate = false; }) },
  ...['active', 'passive', 'on', 'desirable', 'auto'].map((m) => ({ pattern: `channel-group <1-48> mode ${m}`, run: (s, [g]) => channelGroup(s, +g, m) })),
  { pattern: 'no channel-group', run: (s) => s.eachIf((i) => { delete i.channelGroup; }) },
  { pattern: 'spanning-tree portfast', run: (s) => s.eachIf((i) => { i.portfast = true; }) },
  { pattern: 'no spanning-tree portfast', run: (s) => s.eachIf((i) => { i.portfast = false; }) },
  { pattern: 'spanning-tree bpduguard enable', run: (s) => s.eachIf((i) => { i.bpduguard = true; }) },
  { pattern: 'spanning-tree cost <1-200000000>', run: (s, [c]) => s.eachIf((i) => { i.stpCost = +c; }) },
  { pattern: 'no spanning-tree cost', run: (s) => s.eachIf((i) => { delete i.stpCost; }) },
  { pattern: 'spanning-tree port-priority <0-240>', run: (s, [p]) => (+p % 16 ? ['% Port Priority in increments of 16 is required'] : s.eachIf((i) => { i.stpPriority = +p; })) },
  { pattern: 'ip address <A.B.C.D> <A.B.C.D>', run: (s, [a, m]) => needSvi(s, () => s.ipAddress(a, m)) },
  { pattern: 'no ip address', run: (s) => needSvi(s, () => s.eachIf((i) => { i.ip = null; })) },
]);

export const R_IF = K(['router'], [
  { pattern: 'encapsulation dot1q <1-4094>', run: (s, [v]) => s.encap(+v, false) },
  { pattern: 'encapsulation dot1q <1-4094> native', run: (s, [v]) => s.encap(+v, true) },
  { pattern: 'no encapsulation dot1q', run: (s) => s.eachIf((i) => { i.encap = null; }) },
]);

/** Running-config fragments for switching features. */
export function switchIfLines(d, n, i) {
  const out = [];
  if (isSvi(n)) {
    if (i.ip) out.push(` ip address ${i.ip.ip} ${i.ip.mask}`); else out.push(' no ip address');
    return out;
  }
  if (i.mode === 'trunk') {
    if (d.l3 && i.trunkEncap) out.push(` switchport trunk encapsulation ${i.trunkEncap}`);
    if (i.nativeVlan && i.nativeVlan !== 1) out.push(` switchport trunk native vlan ${i.nativeVlan}`);
    if (i.allowed) out.push(` switchport trunk allowed vlan ${fmtVlanList(i.allowed)}`);
    out.push(' switchport mode trunk');
  } else {
    if (i.accessVlan !== 1) out.push(` switchport access vlan ${i.accessVlan}`);
    if (i.explicitAccess) out.push(' switchport mode access');
  }
  if (i.nonegotiate) out.push(' switchport nonegotiate');
  if (i.channelGroup) out.push(` channel-group ${i.channelGroup.id} mode ${i.channelGroup.mode}`);
  if (i.stpCost) out.push(` spanning-tree cost ${i.stpCost}`);
  if (i.stpPriority != null && i.stpPriority !== 128) out.push(` spanning-tree port-priority ${i.stpPriority}`);
  if (i.portfast) out.push(' spanning-tree portfast');
  if (i.bpduguard) out.push(' spanning-tree bpduguard enable');
  return out;
}
export function switchGlobalLines(d) {
  const out = [`spanning-tree mode ${d.stp?.mode || 'pvst'}`, 'spanning-tree extend system-id'];
  for (const [v, p] of Object.entries(d.stp?.priority || {}).sort((a, b) => a[0] - b[0])) out.push(`spanning-tree vlan ${v} priority ${p}`);
  out.push('!');
  for (const v of Object.keys(d.vlans).map(Number).filter((x) => x > 1 && x < 1002).sort((a, b) => a - b)) out.push(`vlan ${v}`, ` name ${d.vlans[v].name}`, '!');
  if (d.ipRouting) out.push('ip routing', '!');
  return out;
}
export { physFor, carries, linkOf, linkState };
